/**
 * Irish public holiday calendar.
 *
 * Public holiday hours attract additional pay, so the dates have to be derived
 * rather than hard-coded: most are movable, and the February holiday was only
 * introduced in 2023.
 */

import { formatDateKey } from './time';

export interface PublicHoliday {
  dateKey: string;
  name: string;
}

/** Anonymous Gregorian computus, then +1 day for Easter Monday. */
function getEasterMondayDateKey(year: number): string {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = ((19 * a) + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + (2 * e) + (2 * i) - h - k) % 7;
  const m = Math.floor((a + (11 * h) + (22 * l)) / 451);
  const month = Math.floor((h + l - (7 * m) + 114) / 31);
  const day = ((h + l - (7 * m) + 114) % 31) + 1;
  const easterSunday = new Date(Date.UTC(year, month - 1, day));
  easterSunday.setUTCDate(easterSunday.getUTCDate() + 1);
  return formatDateKey(easterSunday);
}

function nthWeekdayDateKey(year: number, monthIndex: number, weekdayIndex: number, nth: number): string {
  const date = new Date(Date.UTC(year, monthIndex, 1));
  const offset = (weekdayIndex - date.getUTCDay() + 7) % 7;
  date.setUTCDate(1 + offset + ((nth - 1) * 7));
  return formatDateKey(date);
}

function lastWeekdayDateKey(year: number, monthIndex: number, weekdayIndex: number): string {
  const date = new Date(Date.UTC(year, monthIndex + 1, 0));
  const offset = (date.getUTCDay() - weekdayIndex + 7) % 7;
  date.setUTCDate(date.getUTCDate() - offset);
  return formatDateKey(date);
}

export function getIrishPublicHolidays(year: number): PublicHoliday[] {
  const holidays: PublicHoliday[] = [
    { dateKey: `${year}-01-01`, name: 'New Year' },
    { dateKey: `${year}-03-17`, name: "St Patrick's Day" },
    { dateKey: getEasterMondayDateKey(year), name: 'Easter Monday' },
    { dateKey: nthWeekdayDateKey(year, 4, 1, 1), name: 'First Monday in May' },
    { dateKey: nthWeekdayDateKey(year, 5, 1, 1), name: 'First Monday in June' },
    { dateKey: nthWeekdayDateKey(year, 7, 1, 1), name: 'First Monday in August' },
    { dateKey: lastWeekdayDateKey(year, 9, 1), name: 'Last Monday in October' },
    { dateKey: `${year}-12-25`, name: 'Christmas Day' },
    { dateKey: `${year}-12-26`, name: "St Stephen's Day" },
  ];

  // St Brigid's Day, from 2023: 1 February when that is a Friday, else the
  // first Monday in February.
  if (year >= 2023) {
    const firstFebruaryDate = new Date(Date.UTC(year, 1, 1));
    const februaryHoliday = firstFebruaryDate.getUTCDay() === 5
      ? `${year}-02-01`
      : nthWeekdayDateKey(year, 1, 1, 1);
    holidays.splice(1, 0, { dateKey: februaryHoliday, name: 'First Monday in February' });
  }

  return holidays;
}

export function buildIrishHolidaySet(startYear: number, endYear: number): Set<string> {
  const holidays = new Set<string>();
  for (let year = startYear; year <= endYear; year += 1) {
    getIrishPublicHolidays(year).forEach((holiday) => holidays.add(holiday.dateKey));
  }
  return holidays;
}

export function buildIrishHolidayMap(startYear: number, endYear: number): Map<string, string> {
  const holidays = new Map<string, string>();
  for (let year = startYear; year <= endYear; year += 1) {
    getIrishPublicHolidays(year).forEach((holiday) => holidays.set(holiday.dateKey, holiday.name));
  }
  return holidays;
}

export function getYearFromDateKey(dateKey: string): number {
  return Number.parseInt(dateKey.slice(0, 4), 10);
}
